import { API_ROOT } from '../../../config';
import SimpleCache from '../../../modules/SimpleCache';
import ssFetch, { ssFetchJSON } from '../../../modules/ssFetch';
import type { PaymentMethodType } from '../../../components/consumer/booking/BookingFlow/types';
import { IAppointmentCancellationLink } from '../../../modules/Api.types';
import type { ISMSStatus } from '../Clients/types';
import type { ProAppointmentUpdatePayload } from '../../../store/ProviderManageAppointment/ProviderManageAppointment.model';
import { IDRFResponse } from '../../../types';
import { DateTimeStamp } from '../../../types/dateTime';
import type { BookingMethod, ProPromotionDiscount } from '../../Users/Appointments';

const AppointmentCache = new SimpleCache(10);

export enum ClientPreferredCheckoutType {
  Auto = 1,
  Manual = 2,
}

export enum DiscountCodeType {
  Coupon = 1,
  AffiliateCode = 2,
  CIPNewPro = 3,
}

export enum DiscountDiscountType {
  DiscountTypeAmount = 1,
  DiscountTypePercent = 2,
}

export enum CardCheckState {
  Attempted = 'attempted',
  Successful = 'successful',
  FailedError = 'failed_error',
  CardDeclined = 'card_declined',
}

export type DiscountCode = {
  id: number;
  type: DiscountCodeType;
  code: string;
  discount_type: DiscountDiscountType | null;
  discount_amount: string | null;
  discount_percent: string | null;
  max_discount_amount: string | null;
};

export type DiscountCodeRedemption = {
  id: number;
  amount: string | null;
  discount_code: DiscountCode;
};

export type ClientCardOnFile = {
  id: number;
  exp_month: number;
  exp_year: number;
  bank: string;
  last4: string;
  source: number;
  save_for_future?: boolean | null;
};

export type CardCheck = {
  last4: string;
  state_code: CardCheckState;
  bank: string;
};

export type Client = {
  id: number;
  name: string;
  thumbnail: string | null;
  sms_status: ISMSStatus | null;
};

export type NonCardPaymentOption = 'cash' | 'dumb_credit' | 'check' | 'gift_cert';
export type CardPaymentOption = 'stripe_connect';
export type KlarnaPaymentOption = 'klarna';
export type PaymentOption = CardPaymentOption | NonCardPaymentOption | KlarnaPaymentOption;

export type CheckoutPaymentType = {
  amount: string;
  memo: string;
  name: string;
  is_payment: boolean;
};

export type Checkout = {
  client_service_fee_exists: boolean;
  client_service_fee: string;
  cost: string;
  creation_source: string;
  creation_time: DateTimeStamp;
  payment_types: CheckoutPaymentType[] | null;
  sub_total: string;
  taxes: string;
  tip_exists: boolean;
  tip_included_in_total: boolean;
  tip: string;
  total: string;
};

export type TimeBlock = {
  id: number;
  service_name: string;
  service_cost_cents: number;
  service: number;
  abbr_name: string | null;
  service_is_powerbooked: boolean;
  service_padding_time: number;
  start: DateTimeStamp;
  end: DateTimeStamp;
  local_start: DateTimeStamp;
  local_end: DateTimeStamp;
  duration_minutes: string;
};

export enum ReminderType {
  SmsEmailReminder = 1,
  EmailReminder = 2,
  SmsOnlyReminder = 3,
}

export type Reminder = {
  appointment: number;
  confirmed_by_service_time: DateTimeStamp | null;
  creation_time: DateTimeStamp;
  id: number;
  message: string;
  rescheduled: boolean;
  submitted_to_service_time: DateTimeStamp;
  transmitted_from: string | null;
  transmitted_to: string | null;
  type: ReminderType;
};

export enum ConfirmationStatus {
  Unavailable = 1,
  Pending = 2,
  ProBookedConfirmed = 3,
  ClientBookedConfirmed = 4,
  KlarnaPending = 5,
}

/**
 * ProviderAppointmentListItemSerializer
 * GET: api/{v2.1, v2}/providers/{providerId}/appointments
 */
export type ProAppointment = {
  auth_amount: number | null;
  booked_by: number;
  booking_method: BookingMethod;
  can_change_payment_type: boolean;
  cancellation_charge: string | null;
  card_check: CardCheck;
  checkout: Checkout | null;
  client: Client;
  client_card_on_file: ClientCardOnFile | null;
  client_has_card: boolean;
  client_id: string;
  client_note: string;
  client_requested_checkout_type: ClientPreferredCheckoutType | null;
  confirmation_status: ConfirmationStatus;
  cost: string;
  declined_bank: string | null;
  declined_last4: string | null;
  discount_code_redemption: DiscountCodeRedemption | null,
  end: DateTimeStamp;
  followup_note: string | null;
  has_payment_request: boolean;
  id: number;
  in_cancellation_charge_window: boolean;
  is_autocharge: boolean;
  is_autocheckout: boolean;
  is_booked_online: boolean;
  is_cancelled: boolean;
  is_checked_out_late_cancel: boolean;
  is_checked_out_noshow: boolean;
  is_expresspay: boolean;
  is_monetized_new_client_delivery: boolean;
  is_new_client_delivery: boolean;
  is_noshow: boolean;
  is_paid_new_client_delivery: boolean;
  is_premium_appointment: boolean;
  is_prepaid_new_client_delivery: boolean;
  is_refundable: boolean;
  is_user_first_booking_with_pro: boolean;
  latest_client_card_check: CardCheck | null;
  local_creation_time: DateTimeStamp;
  local_end: DateTimeStamp;
  local_start: DateTimeStamp;
  modification_time: DateTimeStamp;
  noshow_charge: string | null;
  note: string | null;
  nslc_charge_declined: boolean;
  order_creation_source: string | null;
  order_status: string | null;
  paid_booking_fee: boolean;
  payment_method_type: PaymentMethodType | null;
  prepay_amount: string | null;
  prepay_status: number | null;
  pro_client_notes: { field: string, value: string }[];
  pro_promotion_discounts: ProPromotionDiscount[];
  product_cost: string;
  provider: number;
  recurrence_interval: number | null;
  scheduled_via_schedule_gap: boolean;
  smart_price_service_upcharge: string | null;
  smart_priced: boolean;
  sms_eligible: boolean;
  start: DateTimeStamp;
  surcharge: string;
  time_blocks: TimeBlock[],
  title: string | null;
  type: number;
  has_ncd_fee_payment_attempt: boolean | null;
};

/**
 * ProviderClientAppointmentGetByIdSerializer
 * GET: api/{v2.1, v2}/providers/{providerId}/appointments/{appointmentId}
 */
export type ProAppointmentDetails = ProAppointment & {
  booked_by_user: string | null;
  creation_time: DateTimeStamp;
  reminders: Reminder[];
  serializer_version: string;
  waitlist_id: number | null;
};

type QueryParams = {
  providerId: number;
  days?: number;
  start_date?: string; // start_date=2022-05-22T00:00:00-04:00
  page?: number;
  size?: 'all' | number;
};

export function getProAppointmentList({
  providerId,
  days,
  start_date: startDate,
  page = 1,
  size,
}: QueryParams): Promise<IDRFResponse<ProAppointment>> {
  const url = new URL(`/api/v2.1/providers/${providerId}/appointments`, API_ROOT);

  url.searchParams.set('page', page.toString());

  if (size) {
    url.searchParams.set('size', size.toString());
  }

  if (days) {
    url.searchParams.set('days', days.toString());
  }

  if (startDate) {
    url.searchParams.set('start_date', startDate.toString());
  }

  return ssFetchJSON<IDRFResponse<ProAppointment>>(
    url.toString(),
  );
}

export function getProAppointmentDetails(
  providerId: number,
  appointmentId: number,
): Promise<ProAppointmentDetails> {
  const url = new URL(
    `/api/v2.1/providers/${providerId}/appointments/${appointmentId}`,
    API_ROOT,
  );

  return ssFetchJSON<ProAppointmentDetails>(
    url.toString(),
    { includeResponseOnError: true },
  );
}

export function updateProAppointment(
  providerId: number,
  appointment: ProAppointmentUpdatePayload,
) {
  const url = new URL(
    `/api/v2.1/providers/${providerId}/appointments/${appointment.id}`,
    API_ROOT,
  );

  return ssFetch(url.toString(), {
    method: 'PATCH',
    body: appointment,
  }).then(res => {
    if (!res.ok && res.status !== 304) { // the endpoint may return 304 on success?
      return res.text().then(Promise.reject.bind(Promise));
    }

    return res.json();
  });
}

export function createProAppointment(
  providerId: number,
  appointment: Partial<ProAppointmentDetails>,
) {
  const url = new URL(
    `/api/v2.1/providers/${providerId}/appointments`,
    API_ROOT,
  );

  return ssFetchJSON(url.toString(), {
    method: 'POST',
    body: appointment,
  });
}

export const getAppointmentCancellationLink = async (
  providerId: number,
  appointmentId: number,
): Promise<IAppointmentCancellationLink> => {
  const url = new URL(`${API_ROOT}/api/v2/providers/${providerId}/appointment/${appointmentId}/manage_appointment_link?cancel=true`);

  return ssFetchJSON(
    url.toString(),
    { ssCache: AppointmentCache },
  );
};

/**
 * Allows provider to remove smart price upcharge from appointment
 *
 * @param providerId: the provider primary key
 * @param appointmentId: the appointment primary key
 * @returns {Promise<boolean>}
 */
export const removeSmartPrice = async (
  providerId: string | number,
  appointmentId: string | number,
): Promise<boolean> => {
  const url = `/api/v2.1/providers/${providerId}/appointments/${appointmentId}/remove-smart-price`;
  const response = await ssFetchJSON(url, { method: 'POST' });
  return response === 'OK';
};
